import React, { useState } from "react";
import { API_BASE_URL } from "../../utils/constants";
import "bootstrap/dist/css/bootstrap.min.css";
import { Form } from "react-bootstrap";
import { FaCheckCircle, FaFilePdf } from "react-icons/fa";
import "./CaseTrackCasewise1.css";

const STAGES = [
  { key: "Uploaded_Date",      label: "Uploaded",    docKey: null },
  { key: "Appointment_Letter", label: "Appt Letter", docKey: "Appointment_Letter_Doc" },
  { key: "Acceptance_Letter",  label: "Acceptance",  docKey: "Acceptance_Letter_Doc" },
  { key: "SOC",                label: "SOC",         docKey: "SOC_Doc" },
  { key: "Sec17_Application",  label: "Sec17 App",   docKey: "Sec17_Application_Doc" },
  { key: "Sec17_Order",        label: "Sec17 Order", docKey: "Sec17_Order_Doc" },
  { key: "First_Hearing",      label: "1st Hearing", docKey: null },
  { key: null, label: "1st Roznama", docKey: "First_Hearing_Roznama_Doc" },
  { key: null, label: "2nd Notice",  docKey: "Second_Hearing_Notice_Doc" },
  { key: "Second_Hearing",     label: "2nd Hearing", docKey: null },
  { key: null, label: "2nd Roznama", docKey: "Second_Hearing_Roznama_Doc" },
  { key: null, label: "3rd Notice",  docKey: "Third_Hearing_Notice_Doc" },
  { key: "Third_Hearing",      label: "3rd Hearing", docKey: null },
  { key: null, label: "3rd Roznama", docKey: "Third_Hearing_Roznama_Doc" },
  { key: null, label: "PURSIS",      docKey: "PURSIS_Doc" },
  { key: "Award",              label: "Award",       docKey: "Award_Doc" },
];

const HEARING_KEYS = ["First_Hearing", "Second_Hearing", "Third_Hearing"];

const fmt = (d) => {
  if (!d) return null;
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return null;
  return `${String(dt.getMonth() + 1).padStart(2, "0")}/${String(dt.getDate()).padStart(2, "0")}/${dt.getFullYear()}`;
};

const statusBadge = (status) => {
  const map = {
    Active: "badge bg-success",
    "On Hold": "badge bg-warning text-dark",
    Terminated: "badge bg-danger",
  };
  return <span className={map[status] || "badge bg-secondary"}>{status}</span>;
};

const handleDownload = async (serviceId, serviceTypeId, label) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/CaseFile?C=${serviceId}&S=${serviceTypeId}`,
      { method: "GET" }
    );
    if (!response.ok) throw new Error("Failed to download document");
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${label}_${serviceId}.pdf`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  } catch (err) {
    console.error("Download error:", err);
    alert("Failed to download document");
  }
};

const DocBtn = ({ doc, label }) => {
  if (!doc) return null;
  return (
    <button
      className="pdf-inline-btn"
      onClick={() => handleDownload(doc.serviceId, doc.serviceTypeId, label)}
      title={`Download ${label}`}
    >
      <FaFilePdf /> PDF
    </button>
  );
};

const StageRow = ({ stage, row }) => {
  const isHearing = HEARING_KEYS.includes(stage.key);
  const meetingNo = stage.key === "First_Hearing" ? 1 : stage.key === "Second_Hearing" ? 2 : 3;
  const dateVal = stage.key ? row[stage.key] : null;
  const docVal  = stage.docKey ? row[stage.docKey] : null;
  const done    = !!(dateVal || docVal);

  const isContested = isHearing && row.Contested_Meeting_No === meetingNo;
  const contestedDate = isContested ? row.Contested_Date : null;

  return (
    <div className="case-row">
      <div className="timeline-col">
        <div className={`timeline-dot ${done ? "active-dot" : "inactive-dot"}`} />
        <div className="timeline-line" />
      </div>

      <div className="case-content">
        <div className="case-header-inline">
          <div className="case-col case-label">{stage.label}</div>

          <div className="case-col case-doc">
            {done && <DocBtn doc={docVal} label={stage.label} />}
          </div>

          <div className="case-col case-date">
            {dateVal ? (
              <span>{fmt(dateVal)}</span>
            ) : docVal ? (
              <FaCheckCircle className="text-success" />
            ) : (
              <span className="text-muted" style={{ fontSize: "13px" }}>Pending</span>
            )}
          </div>
        </div>

        {isContested && contestedDate && (
          <div className="mt-1" style={{ fontSize: "12px" }}>
            <span className="badge bg-warning text-dark me-1">Contested</span>
            <span className="text-muted">{fmt(contestedDate)}</span>
          </div>
        )}
      </div>
    </div>
  );
};

const CaseTrackCasewise1 = () => {
  const [inputRef, setInputRef]     = useState("");
  const [customerInfo, setCustomerInfo] = useState(null);
  const [stageData, setStageData]   = useState(null);
  const [loading, setLoading]       = useState(false);
  const [searched, setSearched]     = useState(false);

  const handleSearch = async () => {
    const trimmed = inputRef.trim();
    if (!trimmed) return;

    setLoading(true);
    setSearched(true);
    setCustomerInfo(null);
    setStageData(null);

    try {
      const [caseRes, misRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/Case?Ref_no=${trimmed}`),
        fetch(`${API_BASE_URL}/api/mis?refNo=${trimmed}`),
      ]);

      const [caseJson, misJson] = await Promise.all([caseRes.json(), misRes.json()]);
      const caseArr = Array.isArray(caseJson) ? caseJson : [];
      const misArr  = Array.isArray(misJson)  ? misJson  : [];

      // /api/Case returns multiple borrower types; pick the primary borrower (Type B)
      const customer = caseArr.find((item) => item.Type === "B") || caseArr[0] || null;
      setCustomerInfo(customer);
      setStageData(misArr[0] || null);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const notFound = searched && !loading && !customerInfo && !stageData;

  return (
    <div className="container py-3">
      {/* Search Bar */}
      <h4 className="mb-3">Case Tracking by Reference No</h4>
      <div className="row g-2 mb-4">
        <div className="col-md-5">
          <Form.Control
            type="text"
            placeholder="Enter Reference No"
            value={inputRef}
            onChange={(e) => setInputRef(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
        </div>
        <div className="col-md-2">
          <button
            className="btn btn-primary w-100"
            onClick={handleSearch}
            disabled={loading || !inputRef.trim()}
          >
            {loading ? "..." : "Search"}
          </button>
        </div>
        <div className="col-md-2">
          <button
            className="btn btn-outline-secondary w-100"
            onClick={() => {
              setInputRef("");
              setCustomerInfo(null);
              setStageData(null);
              setSearched(false);
            }}
          >
            Clear
          </button>
        </div>
      </div>

      {loading && (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {notFound && (
        <div className="alert alert-warning">No case found for reference no: <strong>{inputRef}</strong></div>
      )}

      {!loading && (customerInfo || stageData) && (
        <div className="row mt-2">
          {/* Left — Customer Info */}
          <div className="col-md-5 customCard1 me-3">
            <h5 className="text-center customHeader">Customer Information</h5>

            {[
              ["Reference No",   stageData?.Reference_No  || customerInfo?.Reference_no],
              ["Customer Name",  stageData?.Customer_Name || customerInfo?.Cust_name],
              ["Account No",     stageData?.Account_No    || customerInfo?.Acc_no],
              ["Lot No",         stageData?.Lot_No        || customerInfo?.Lot_no],
              ["Arbitrator",     stageData?.Arbitrator],
              ["Customer Id",    customerInfo?.Cust_id],
              ["Mobile No",      customerInfo?.Mobile_no],
              ["Email",          customerInfo?.Email_id],
              ["Client",         customerInfo?.client_name],
              ["Product",        customerInfo?.Product_name],
            ].map(([label, value]) =>
              value ? (
                <div className="row my-1 ps-3" key={label}>
                  <div className="col-5"><label>{label}</label></div>
                  <div className="col-6">{value}</div>
                </div>
              ) : null
            )}

            {stageData?.Status && (
              <div className="row my-1 ps-3">
                <div className="col-5"><label>Status</label></div>
                <div className="col-6">{statusBadge(stageData.Status)}</div>
              </div>
            )}

            {customerInfo?.Is_terminate === "1" && customerInfo?.Termination_date && (
              <div className="px-3 mt-2">
                <span className="text-danger fw-bold" style={{ fontSize: "13px" }}>
                  Terminated on {fmt(customerInfo.Termination_date)}
                </span>
              </div>
            )}
          </div>

          {/* Right — Stage Timeline */}
          <div className="col-md-6 customCard p-3">
            <h5 className="text-center customHeader mb-3">Case Stage Progress</h5>

            {stageData ? (
              STAGES.map((stage) => (
                <StageRow key={stage.label} stage={stage} row={stageData} />
              ))
            ) : (
              <p className="text-muted text-center">Stage data unavailable.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CaseTrackCasewise1;
